/* ==========================================================================
   GAUI — global.js
   Lean shared runtime. Section-specific behaviour (cart drawer, predictive
   search, product form, gallery) ships as its own module with its section.
   ========================================================================== */
(function () {
  "use strict";

  // Signal JS availability for progressive-enhancement CSS hooks.
  document.documentElement.classList.remove("no-js");
  document.documentElement.classList.add("js");

  /* ---------------------------------------------------- Pub/sub event bus ---
     Sections (cart, search, product form) publish/subscribe without coupling. */
  var subscribers = {};
  window.GAUI = window.GAUI || {};
  window.GAUI.events = {
    publish: function (event, data) {
      (subscribers[event] || []).forEach(function (cb) {
        cb(data);
      });
    },
    subscribe: function (event, cb) {
      subscribers[event] = subscribers[event] || [];
      subscribers[event].push(cb);
      return function unsubscribe() {
        subscribers[event] = subscribers[event].filter(function (fn) {
          return fn !== cb;
        });
      };
    },
  };
  // Canonical event names shared across modules.
  window.GAUI.EVENTS = {
    cartUpdate: "cart:update",
    cartError: "cart:error",
  };

  /* --------------------------------------------------- Scroll reveal --------
     Quiet, purposeful reveal. Honors prefers-reduced-motion via CSS; here we
     only add the class when the element enters the viewport. */
  function initScrollReveal() {
    var targets = document.querySelectorAll("[data-reveal]");
    if (!targets.length || !("IntersectionObserver" in window)) {
      targets.forEach(function (el) {
        el.classList.add("is-revealed");
      });
      return;
    }
    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-revealed");
            observer.unobserve(entry.target);
          }
        });
      },
      { rootMargin: "0px 0px -8% 0px", threshold: 0.05 }
    );
    targets.forEach(function (el) {
      observer.observe(el);
    });
  }

  /* ------------------------------------------- Colorway hover preview ----
     Site-wide: hovering (or focusing) any color dot with data-swatch-img
     previews that colorway on its own card photo — the mobile-menu behavior
     applied universally. Clicks keep each surface's native action (links
     navigate to the sibling product). The menu handles itself. */
  function swatchPreview(sw) {
    var card = sw.closest(".rcard, .product-card, [data-swatch-card]");
    if (!card) return;
    var src = sw.getAttribute("data-swatch-img");
    if (!src) return;
    card.querySelectorAll(".rcard__img, .product-card__image, .hcsw__photo, .hsbc__photo, .srch__product-img, .mega__product-img").forEach(function (img) {
      if (img.getAttribute("src") !== src) {
        img.removeAttribute("srcset");
        img.setAttribute("src", src);
      }
    });
    var group = sw.parentElement;
    if (group) {
      group.querySelectorAll("[data-swatch-img]").forEach(function (s) {
        s.classList.toggle("is-active", s === sw);
      });
    }
  }
  document.addEventListener("mouseover", function (e) {
    var sw = e.target.closest ? e.target.closest("[data-swatch-img]") : null;
    if (!sw || sw.closest(".mmenu")) return;
    swatchPreview(sw);
  });
  // Non-link dots (role=button spans, e.g. search cards) activate on click/Enter/Space
  // — same in-place swap the menu does. Link dots keep navigating.
  document.addEventListener("click", function (e) {
    var sw = e.target.closest ? e.target.closest('span[role="button"][data-swatch-img]') : null;
    if (!sw || sw.closest(".mmenu")) return;
    e.preventDefault();
    e.stopPropagation();
    swatchPreview(sw);
  });
  document.addEventListener("keydown", function (e) {
    if (e.key !== "Enter" && e.key !== " ") return;
    var sw = e.target.closest ? e.target.closest('span[role="button"][data-swatch-img]') : null;
    if (!sw || sw.closest(".mmenu")) return;
    e.preventDefault();
    swatchPreview(sw);
  });

  if (document.readyState !== "loading") {
    initScrollReveal();
  } else {
    document.addEventListener("DOMContentLoaded", initScrollReveal);
  }
  // Theme editor re-renders section HTML in place; re-arm the observer so
  // fresh [data-reveal] nodes don't sit stranded in their pre-reveal state.
  // Idempotent: revealed nodes are unobserved, re-observing is harmless.
  document.addEventListener("shopify:section:load", initScrollReveal);

  /* ---------------------------------------------- tap-sweep for CTA links --
     On touch devices (no hover), navigation pills tagged .btn-sweep play
     their color sweep on tap before routing — the same choreography as the
     menu's Shop the Collection. Reduced-motion routes immediately; pageshow
     clears the state after a back-button return. */
  document.addEventListener("click", function (e) {
    var link = e.target.closest ? e.target.closest("a.btn-sweep[href], a.btn-sweep--ink[href]") : null;
    if (!link) return;
    if (window.matchMedia("(hover: hover)").matches) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    if (link.classList.contains("is-sweeping")) return;
    e.preventDefault();
    link.classList.add("is-sweeping");
    window.setTimeout(function () { window.location.href = link.href; }, 380);
  });
  window.addEventListener("pageshow", function () {
    document.querySelectorAll("a.is-sweeping").forEach(function (a) { a.classList.remove("is-sweeping"); });
  });
})();

  // Swatch warm-up: prefetch a card's sibling colorway photos the first time
  // a pointer shows intent, so dot taps/hovers swap instantly (touch fires
  // mouseover on first tap - the follow-up tap is warm). Smoothness pass.
  document.addEventListener("mouseover", function (e) {
    var card = e.target.closest("[data-swatch-card], .rcard, .product-card");
    if (!card || card.dataset.swWarmed) return;
    card.dataset.swWarmed = "1";
    card.querySelectorAll("[data-swatch-img]").forEach(function (sw) {
      var src = sw.getAttribute("data-swatch-img");
      if (src) { new Image().src = src; }
    });
  }, { passive: true });
